import { stripHtmlTags } from '@chiroup/core/functions/stripHtmlTags';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/16/solid';
import {
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  DocumentIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import { PortalNavigationState } from '../AuthenticatedNotFoundRedirect';
import FallbackImage from '../common/FallbackImage';

const S3_ASSETS_URL = import.meta.env.VITE_S3_ASSETS_URL as string;

const HealthHub: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { state } = useLocation();
  const { selectedPatient, language } = usePatientPortal();
  const [showNotFoundNotice, setShowNotFoundNotice] = useState(
    Boolean((state as PortalNavigationState | null)?.pageNotFound)
  );
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';

  const exercises = selectedPatient?.exercises || [];
  const conditions = selectedPatient?.conditions || [];
  const advice = selectedPatient?.advice || [];
  const files = selectedPatient?.fileAttachments || [];

  const activeExercises = exercises.filter((e: any) => e.phase !== 2);
  const hasUpcoming = exercises.length > activeExercises.length;

  const hasAnyContent =
    exercises.length > 0 || conditions.length > 0 || advice.length > 0 || files.length > 0;

  return (
    <div className="flex flex-col gap-7">
      {showNotFoundNotice && (
        <div
          className="flex items-start gap-2 rounded-xl bg-gray-50 px-3.5 py-3 text-gray-600 ring-1 ring-gray-950/5 dark:bg-white/5 dark:text-gray-300 dark:ring-white/10"
          role="status"
        >
          <InformationCircleIcon className="size-4 shrink-0 fill-gray-400 dark:fill-gray-500" />
          <p className="min-w-0 flex-1 text-sm/5">
            We couldn’t find that page, so we brought you to My Health.
          </p>
          <button
            type="button"
            className="shrink-0 rounded-md p-0.5 hover:bg-gray-950/5 focus:outline-none focus:ring-2 focus:ring-primary dark:hover:bg-white/10"
            aria-label="Dismiss page not found notice"
            onClick={() => setShowNotFoundNotice(false)}
          >
            <XMarkIcon className="size-4 fill-gray-400 dark:fill-gray-500" />
          </button>
        </div>
      )}

      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {hcVerbiage.health[language]}
        </h2>
        <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
          {hcVerbiage.healthDescription[language]}
        </p>
      </div>

      {!hasAnyContent && (
        <div className="portal-card px-6 py-16 text-center">
          <HeartIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
          <p className="mt-3 text-sm text-gray-400 dark:text-gray-500">
            {language === 'es'
              ? 'No hay contenido de salud disponible todavía.'
              : 'No health content available yet.'}
          </p>
        </div>
      )}

      {/* Exercises */}
      {exercises.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <AcademicCapIcon className="h-4.5 w-4.5" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                {hcVerbiage.exercises[language]}
              </h3>
            </div>
            <Link
              to={`${basePath}/exercises`}
              className="text-sm font-semibold"
              style={{ color: primaryColor }}
            >
              {language === 'es' ? 'Ver Todo' : 'See All'}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {activeExercises.map((exercise: any) => (
              <Link
                key={exercise.ID}
                to={`${basePath}/exercises/${exercise.ID}`}
                className="portal-card flex items-center gap-3 p-3.5 active:scale-[0.98] transition-transform"
              >
                <FallbackImage
                  src={
                    exercise.img
                      ? `${exercise.img}${exercise.img.includes('?') ? '&' : '?'}w=88&q=80`
                      : null
                  }
                  alt=""
                  imgClassName="shrink-0 w-11 h-11 rounded-xl object-cover"
                  fallback={
                    <div
                      className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}15` }}
                    >
                      <AcademicCapIcon className="h-5 w-5" style={{ color: primaryColor }} />
                    </div>
                  }
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {exercise.name}
                  </p>
                  {exercise.descr && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                      {stripHtmlTags(exercise.descr)}
                    </p>
                  )}
                </div>
                <ChevronRightIcon className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0" />
              </Link>
            ))}
          </div>
          {hasUpcoming && (
            <Link
              to={`${basePath}/exercises`}
              className="block mt-2.5 text-center text-xs font-medium"
              style={{ color: primaryColor }}
            >
              {language === 'es'
                ? `Ver todos los ${exercises.length} ejercicios incluyendo próximos`
                : `See all ${exercises.length} exercises including upcoming`}
            </Link>
          )}
        </section>
      )}

      {/* Conditions */}
      {conditions.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <HeartIcon className="h-4.5 w-4.5" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                {hcVerbiage.conditions[language]}
              </h3>
            </div>
            <Link
              to={`${basePath}/conditions`}
              className="text-sm font-semibold"
              style={{ color: primaryColor }}
            >
              {language === 'es' ? 'Ver Todo' : 'See All'}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {conditions.map((condition: any) => (
              <Link
                key={condition.ID}
                to={`${basePath}/conditions/${condition.ID}`}
                className="portal-card flex items-center gap-3 p-3.5 active:scale-[0.98] transition-transform"
              >
                <FallbackImage
                  src={
                    condition.commonImg
                      ? condition.commonImg.startsWith('http')
                        ? condition.commonImg
                        : `${S3_ASSETS_URL}/${condition.commonImg}`
                      : null
                  }
                  alt=""
                  imgClassName="shrink-0 w-11 h-11 rounded-xl object-cover"
                  fallback={
                    <div
                      className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}15` }}
                    >
                      <HeartIcon className="h-5 w-5" style={{ color: primaryColor }} />
                    </div>
                  }
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {condition.commonName}
                  </p>
                  {condition.descr && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                      {stripHtmlTags(condition.descr)}
                    </p>
                  )}
                </div>
                <ChevronRightIcon className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Advice */}
      {advice.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <ClipboardDocumentCheckIcon
                  className="h-4.5 w-4.5"
                  style={{ color: primaryColor }}
                />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                {hcVerbiage.advice[language]}
              </h3>
            </div>
            <Link
              to={`${basePath}/advice`}
              className="text-sm font-semibold"
              style={{ color: primaryColor }}
            >
              {language === 'es' ? 'Ver Todo' : 'See All'}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {advice.map((item: any) => (
              <Link
                key={item.ID}
                to={`${basePath}/advice/${item.ID}`}
                className="portal-card flex items-center gap-3 p-3.5 active:scale-[0.98] transition-transform"
              >
                <FallbackImage
                  src={item.icon}
                  alt=""
                  imgClassName="shrink-0 w-11 h-11 rounded-xl object-cover"
                  fallback={
                    <div
                      className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}15` }}
                    >
                      <ClipboardDocumentCheckIcon
                        className="h-5 w-5"
                        style={{ color: primaryColor }}
                      />
                    </div>
                  }
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {item.name}
                  </p>
                  {item.descr && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                      {stripHtmlTags(item.descr)}
                    </p>
                  )}
                </div>
                <ChevronRightIcon className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Files — patient-specific documents (intake forms, lab results,
          reports). Moved here from the Advice page since "advice" is
          general guidance, not records, and burying files there made
          them hard to find. */}
      {files.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <DocumentIcon className="h-4.5 w-4.5" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                {hcVerbiage.files[language]}
              </h3>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {files.map((file: any, i: number) => (
              <a
                key={i}
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="portal-card flex items-center gap-3 p-3.5 active:scale-[0.98] transition-transform"
              >
                <div
                  className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <DocumentIcon className="h-5 w-5" style={{ color: primaryColor }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {file.name}
                  </p>
                </div>
                <ChevronRightIcon className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0" />
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default HealthHub;
